/**
 * Error Taxonomy Mapping — Wave 16
 *
 * Maps raw backend error responses and streaming error events into the
 * unified ClassifiedError structure used by the UI.
 *
 * The backend (Wave 12) emits errors with:
 *   { category, code, message, retryable, timestamp, traceId, spanId, hint }
 *
 * API responses may embed this inside the standard envelope:
 *   { ok: false, error: { ... } }
 *
 * Streaming events deliver it as an OrchestrationStreamEvent with type 'phase_error'.
 */

import type {
  ClassifiedError,
  ErrorCategory,
  ERROR_CATEGORIES,
} from '../types/observability';

// ── Human-readable labels ──────────────────────────────

const CATEGORY_LABELS: Record<ErrorCategory, string> = {
  ORCHESTRATION_ERROR: 'Orchestration Error',
  RETRIEVAL_ERROR: 'Retrieval Error',
  EXECUTION_ERROR: 'Execution Error',
  REFLECTION_ERROR: 'Reflection Error',
  MEMORY_ERROR: 'Memory Error',
  PLUGIN_ERROR: 'Plugin Error',
  PROVIDER_ERROR: 'Provider Error',
  UNKNOWN_ERROR: 'Unknown Error',
};

const CATEGORY_ICONS: Record<ErrorCategory, string> = {
  ORCHESTRATION_ERROR: '🔄',
  RETRIEVAL_ERROR: '🔍',
  EXECUTION_ERROR: '⚡',
  REFLECTION_ERROR: '🪞',
  MEMORY_ERROR: '🧠',
  PLUGIN_ERROR: '🧩',
  PROVIDER_ERROR: '☁️',
  UNKNOWN_ERROR: '❓',
};

const CATEGORY_COLORS: Record<ErrorCategory, string> = {
  ORCHESTRATION_ERROR: 'var(--nc-color-error, #e53e3e)',
  RETRIEVAL_ERROR: 'var(--nc-color-warning, #dd6b20)',
  EXECUTION_ERROR: 'var(--nc-color-error, #e53e3e)',
  REFLECTION_ERROR: 'var(--nc-color-warning, #dd6b20)',
  MEMORY_ERROR: 'var(--nc-color-error, #e53e3e)',
  PLUGIN_ERROR: 'var(--nc-color-warning, #dd6b20)',
  PROVIDER_ERROR: 'var(--nc-color-error, #e53e3e)',
  UNKNOWN_ERROR: 'var(--nc-color-text-muted, #718096)',
};

// ── Validation ─────────────────────────────────────────

const VALID_CATEGORIES = new Set<string>([
  'ORCHESTRATION_ERROR',
  'RETRIEVAL_ERROR',
  'EXECUTION_ERROR',
  'REFLECTION_ERROR',
  'MEMORY_ERROR',
  'PLUGIN_ERROR',
  'PROVIDER_ERROR',
  'UNKNOWN_ERROR',
]);

function isValidCategory(val: unknown): val is ErrorCategory {
  return typeof val === 'string' && VALID_CATEGORIES.has(val);
}

// ── Core mapper ────────────────────────────────────────

/**
 * Map a raw backend error payload (from API response or streaming event) into
 * a `ClassifiedError`.  Gracefully degrades for unknown shapes.
 */
export function mapBackendErrorToUIError(raw: unknown): ClassifiedError {
  // Already classified
  if (isClassifiedError(raw)) return raw;

  const obj = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

  // The backend envelope may nest the real error under `.error`
  const inner =
    typeof obj.error === 'object' && obj.error !== null
      ? (obj.error as Record<string, unknown>)
      : obj;

  const category: ErrorCategory = isValidCategory(inner.category)
    ? inner.category
    : isValidCategory(obj.category)
      ? obj.category
      : 'UNKNOWN_ERROR';

  const code = typeof inner.code === 'string' ? inner.code : 'ERR_UNKNOWN';

  const message =
    typeof inner.message === 'string'
      ? inner.message
      : typeof obj.message === 'string'
        ? obj.message
        : 'An unexpected error occurred.';

  const retryable =
    typeof inner.retryable === 'boolean'
      ? inner.retryable
      : typeof obj.retryable === 'boolean'
        ? obj.retryable
        : false;

  const timestamp =
    typeof inner.timestamp === 'string'
      ? inner.timestamp
      : typeof obj.timestamp === 'string'
        ? obj.timestamp
        : new Date().toISOString();

  return {
    category,
    code,
    message,
    retryable,
    timestamp,
    traceId: asOptionalString(inner.traceId ?? obj.traceId),
    spanId: asOptionalString(inner.spanId ?? obj.spanId),
    hint: asOptionalString(inner.hint ?? obj.hint),
    httpStatus: typeof obj.status === 'number' ? obj.status : undefined,
    raw: obj,
  };
}

/**
 * Map an Axios error (or generic Error) into ClassifiedError.
 */
export function mapAxiosErrorToUIError(err: unknown): ClassifiedError {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { data?: unknown; status?: number } };
    if (axiosErr.response?.data) {
      const classified = mapBackendErrorToUIError(axiosErr.response.data);
      if (!classified.httpStatus && axiosErr.response.status) {
        classified.httpStatus = axiosErr.response.status;
      }
      return classified;
    }
  }

  if (err instanceof Error) {
    return {
      category: 'UNKNOWN_ERROR',
      code: 'ERR_CLIENT',
      message: err.message,
      retryable: false,
      timestamp: new Date().toISOString(),
    };
  }

  return mapBackendErrorToUIError(err);
}

/**
 * Map a streaming event error payload.
 */
export function mapStreamingErrorToUIError(event: Record<string, unknown>): ClassifiedError {
  if (event.error && typeof event.error === 'object') {
    return mapBackendErrorToUIError(event.error);
  }
  return mapBackendErrorToUIError(event);
}

// ── Display helpers ────────────────────────────────────

export function getCategoryLabel(category: ErrorCategory): string {
  return CATEGORY_LABELS[category] ?? 'Unknown Error';
}

export function getCategoryIcon(category: ErrorCategory): string {
  return CATEGORY_ICONS[category] ?? '❓';
}

export function getCategoryColor(category: ErrorCategory): string {
  return CATEGORY_COLORS[category] ?? 'var(--nc-color-text-muted, #718096)';
}

export function formatErrorTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Guards ─────────────────────────────────────────────

export function isClassifiedError(val: unknown): val is ClassifiedError {
  if (typeof val !== 'object' || val === null) return false;
  const obj = val as Record<string, unknown>;
  return (
    isValidCategory(obj.category) &&
    typeof obj.code === 'string' &&
    typeof obj.message === 'string' &&
    typeof obj.retryable === 'boolean'
  );
}

// ── Internal ───────────────────────────────────────────

function asOptionalString(val: unknown): string | undefined {
  return typeof val === 'string' && val.length > 0 ? val : undefined;
}
